import React from "react"
import { Link, Button, FormLabel, ButtonGroup, Typography, Input } from "@one-thd/sui-atomic-components";
import StencilWebAuthnModal from "./StencilWebAuthnModal.component";
import { getOTPv2 } from "../../../../api/custAccountApi";
import { MESSAGE, MESSAGE_TYPE } from "../../../../constants/messages";
import { useCommonContext } from "../../CommonProvider"
import { FieldError } from "../../../common/fields";
import { useCustomerAccount } from "../../AccountProvider";
import { passwordlessEnrollmentVerifyOTP, submitWebAuthnEnrollment } from "../../../../api/custAccountApi"
import {
  clearWebAuthnInfoNative,
  extractCredentialIdFromAttestationResult,
  getScriptFromFrState,
  setNativeWebAuthnEnrollmentSuccess,
  setWebauthnEnrollmentSuccess
} from "../../../../utils/webauthnUtils"
import {
  isNativeApp,
  isWebauthnEnabledForNotNativeAppUsers,
  postDataToEventBus,
  subscribeToLifeCycleEvent,
  subscribeToEvent,
} from "../../../../utils";
import newRelic from "../../../../utils/newrelic";
import analytics from "../../../../utils/analytics";
import { getErrorCode } from "../../../../utils/errors";
import { useNativeWebAuthnData } from "../../NativeWebAuthnProvider";

const WebAuthnEnrollment = ({ webAuthnGlobalState, email, setWebAuthnGlobalState, errors, errorCleanUp, setErrors, flow, setFlow }) => {
  const {addAlert} = useCommonContext();
  const [frState, setFrState] = React.useState(null);
  const context = useCustomerAccount();
  const { setStale } = context;
  const { nativeWebAuthnData, setNativeWebAuthnData } = useNativeWebAuthnData();

  const closeWebAuthnModal = () => {
    setWebAuthnGlobalState({
      ...webAuthnGlobalState,
      isWebAuthnModalOpen: false,
      valueOTP: "",
      isOTPValid: false
    });
    errorCleanUp();
    setFlow("OTP");
  }

  const filterTheInput = (event) => {
    errorCleanUp();
    let hasBeenVerified = false;
    const otp = event.target.value.trim().replace(/[^\d]/,"");
    const regex = /^[0-9]{1,6}$/
    const found = otp.match(regex);
    if (found && otp.length === 6) {
      hasBeenVerified = true
    }
    setWebAuthnGlobalState({
      ...webAuthnGlobalState,
      valueOTP: otp,
      isOTPValid: hasBeenVerified
    });
  }

  const handleEmailMeANewCode = () => {
    setFlow("OTP")
    errorCleanUp();
    setWebAuthnGlobalState({
      ...webAuthnGlobalState,
      isModalLoading: true
    });
    getOTPv2(email)
      .then(({response, error})=> {
        if (response?.data?.encryptedPayload) {
          setWebAuthnGlobalState({
            ...webAuthnGlobalState,
            isModalLoading: false,
            getOTPEncryptedPayload: response.data.encryptedPayload
          });
        } else if (error) {
          setWebAuthnGlobalState({
            ...webAuthnGlobalState,
            isModalLoading: false,
            isWebAuthnModalOpen: false
          });
          addAlert(MESSAGE_TYPE.DANGER, MESSAGE.SOMETHING_WENT_WRONG);
        }
      })
      .catch(() => {
        setWebAuthnGlobalState({
          ...webAuthnGlobalState,
          isModalLoading: false,
          isWebAuthnModalOpen: false
        });
        addAlert(MESSAGE_TYPE.DANGER, MESSAGE.SOMETHING_WENT_WRONG);
      })
  }

  const handleSubmitOnEnterKey = (event) => {
    event.preventDefault();
    if(webAuthnGlobalState.isOTPValid) {
      handleWebAuthnOTPSubmit();
    }
  }

  const handleWebAuthnOTPSubmit = () => {
    let promiseForErrorsReject;

    const promiseErrorHandling = new Promise((resolve, reject)=>{
      promiseForErrorsReject=reject;
    })

    setWebAuthnGlobalState({
      ...webAuthnGlobalState,
      isModalLoading: true,
    });

    passwordlessEnrollmentVerifyOTP(email, webAuthnGlobalState.getOTPEncryptedPayload, webAuthnGlobalState.valueOTP, (isWebauthnEnabledForNotNativeAppUsers() && webAuthnGlobalState.isCurrentDeviceWebAuthnCapable) ? "WEBAUTHN" : "OTP")
      .then(({response, error}) => {
        if (response?.data?.enrollmentCompleted && (response.data?.frState || isNativeApp())) {
          // Successfully resolved the verifyOTP request.
          webAuthnGlobalState.verifyOTPPromiseResolve({ verifyOTPEnrollmentCompleted: true });

          // FLOW
          setFlow("WEBAUTHN")

          if (isNativeApp()) {
            newRelic.nativeAppWebAuthnUpgradeStart("WEBAUTHN_ENROLLMENT_INIT");
            // Send message to native app to start the WebAuthn enrollment on their side
            postDataToEventBus("WEBAUTHN_ENROLLMENT_INIT");
            // Listen for the result of the native enrollment
            subscribeToEvent("SEND_WEBAUTHN_RESULT", (payload) => {
              newRelic.nativeAppWebAuthnSubscription("SEND_WEBAUTHN_RESULT", payload);
              const { success, frUuid, error } = payload;
              if (success) {
                submitWebAuthnEnrollment(null, frUuid);
                const newWebAuthnData = setNativeWebAuthnEnrollmentSuccess(nativeWebAuthnData, email, frUuid);
                setNativeWebAuthnData(newWebAuthnData);
                setWebAuthnGlobalState({
                  ...webAuthnGlobalState,
                  isModalLoading: false,
                  successfulWebauthnDeviceEnrollment: true,
                  isWebAuthnModalOpen: false,
                });
                setStale();
                addAlert(MESSAGE_TYPE.SUCCESS, MESSAGE.SUCCESSFUL_ENROLLMENT_WEBAUTHN_ALERT );
                analytics.postSettingsSubmissionEventWebauthn();
                newRelic.nativeAppWebAuthnSuccess("WEBAUTHN_ENROLLMENT_SUCCESS");
              } else {
                webAuthnGlobalState.forgeRockPromiseReject();
                newRelic.nativeAppWebAuthnFailure("WEBAUTHN_ENROLLMENT_FAILURE", error);
                clearWebAuthnInfoNative(email, nativeWebAuthnData, setNativeWebAuthnData);
              }
            });
          } else {
            // Execute FR script to trigger WebAuthn enrollment for DTW/MW
            const frStateFromResponse = response.data.frState;
            setFrState(frStateFromResponse);
            const webAuthSignInJSPayload = getScriptFromFrState(frStateFromResponse);
            Function(webAuthSignInJSPayload)();
            // Keep track of the ForgeRock state object
            setFrState(frStateFromResponse);
          }

          newRelic.logWebAuthnStartingFlow("WebAuthn Upgrade Flow")

          // Update the global state
          setWebAuthnGlobalState({
            ...webAuthnGlobalState,
            isWebAuthnEnrolling: true,
            isModalLoading: true,
          });

        } else if(response?.data?.otpVerified === false) {
          setErrors(["This code is incorrect."]);
          promiseForErrorsReject({origin: "This code is incorrect", encryptedPayload: response?.data?.encryptedPayload });
        } else  {
          promiseForErrorsReject({origin: error});
        }
      })
      .catch((e)=>{
        console.log(e)
      })

    // Error call handling
    promiseErrorHandling
      .catch(e => {
        if(e?.origin==="This code is incorrect") {
          return  setWebAuthnGlobalState({
            ...webAuthnGlobalState,
            successfulWebauthnDeviceEnrollment: false,
            isWebAuthnModalOpen: true,
            isModalLoading: false,
            getOTPEncryptedPayload: e?.encryptedPayload
          })
        }
        setWebAuthnGlobalState({
          ...webAuthnGlobalState,
          successfulWebauthnDeviceEnrollment: false,
          isWebAuthnModalOpen: false,
          isModalLoading: false
        })
        addAlert(MESSAGE_TYPE.DANGER, MESSAGE.SOMETHING_WENT_WRONG);
      });

    // Result handling for MW/DTW
    Promise.allSettled([webAuthnGlobalState.verifyOTPWebAuthnPromise, webAuthnGlobalState.forgeRockScriptPromise ])
      .then(([verifyOTPResult, webAuthnResult]) => {
        if (verifyOTPResult.status==="fulfilled" && verifyOTPResult.value.verifyOTPEnrollmentCompleted===true && webAuthnResult.status==="fulfilled") {
          setWebAuthnGlobalState({
            ...webAuthnGlobalState,
            successfulWebauthnDeviceEnrollment: true,
            isWebAuthnModalOpen: false,
          })
          setStale();
          addAlert(MESSAGE_TYPE.SUCCESS, MESSAGE.SUCCESSFUL_ENROLLMENT_WEBAUTHN_ALERT );
          analytics.postSettingsSubmissionEventWebauthn();
        } else {
          addAlert(MESSAGE_TYPE.DANGER, MESSAGE.WEBAUTHN_FAILURE_MESSAGE);
          setWebAuthnGlobalState({
            ...webAuthnGlobalState,
            successfulWebauthnDeviceEnrollment: false,
            isWebAuthnModalOpen: false,
          })
          analytics.postErrorEvent("Security Page: Upgrade WebAuthn", "GEN_ERR_500", MESSAGE.WEBAUTHN_FAILURE_MESSAGE);
        }
      });
  }

  const webAuthRef = React.useRef();
  const postWebAuthClick = () => {


    const webAuthnOutcome = webAuthRef.current?.value;
    frState.callbacks[2].input[0].value = webAuthnOutcome;

    setWebAuthnGlobalState({
      ...webAuthnGlobalState,
      isSubmittingWebAuthnResult: true,
      isModalLoading: true
    })

    if (webAuthnOutcome?.includes("ERROR::")) {
      setWebAuthnGlobalState({
        ...webAuthnGlobalState,
        isEnrollmentUnsuccessful: true,
        isClientError: true,
        isModalLoading: false,
        isWebAuthnModalOpen: false
      })
      addAlert(MESSAGE_TYPE.DANGER, MESSAGE.WEBAUTHN_FAILURE_MESSAGE);
      newRelic.logWebAuthnFailure({webAuthnOutcome}, `WebAuthn Upgrade: Script executed and failed: ${webAuthnOutcome}`);
      analytics.postErrorEvent("Security Page: Upgrade WebAuthn", "GEN_ERR_500", MESSAGE.WEBAUTHN_FAILURE_MESSAGE);
    } else {
      const webAuthnDeviceId = extractCredentialIdFromAttestationResult(webAuthnOutcome);
      submitWebAuthnEnrollment(frState, webAuthnDeviceId)
        .then(({response, error})=>{
          if(response){
            setWebauthnEnrollmentSuccess(email, webAuthnDeviceId, frState)
            webAuthnGlobalState.forgeRockPromiseResolve();

            setWebAuthnGlobalState({
              ...webAuthnGlobalState,
              isEnrollmentUnsuccessful: false,
              isClientError: false,
            })
            newRelic.logWebAuthnSuccess("Flow: WebAuthn Upgrade");
          } else if(error) {
            webAuthnGlobalState.forgeRockPromiseReject();
            addAlert(MESSAGE_TYPE.DANGER, MESSAGE.WEBAUTHN_FAILURE_MESSAGE);
            newRelic.logWebAuthnFailure({webAuthnOutcome}, `WebAuthn Upgrade: BE sent an error response: ${webAuthnOutcome}`);
            const errorCode = getErrorCode(error)
            analytics.postErrorEvent("Security Page: Upgrade WebAuthn", errorCode, MESSAGE.WEBAUTHN_FAILURE_MESSAGE);
          }
        })
        .catch(e =>{
          console.log(e);
          addAlert(MESSAGE_TYPE.DANGER, MESSAGE.WEBAUTHN_FAILURE_MESSAGE);
          newRelic.logWebAuthnFailure({webAuthnOutcome}, `WebAuthn Upgrade: Request Failed: ${webAuthnOutcome}`)
          analytics.postErrorEvent("Security Page: Upgrade WebAuthn", "GEN_ERR_500", MESSAGE.WEBAUTHN_FAILURE_MESSAGE);
        })
    }
  };

  return (
    <>
        <StencilWebAuthnModal
          isOpen={webAuthnGlobalState.isWebAuthnModalOpen}
          isLoading={webAuthnGlobalState.isModalLoading}
          flow={flow}
          closeModal={closeWebAuthnModal}
          headerText="Verify Your Identity"
          email={email}>
            <form onSubmit={ handleSubmitOnEnterKey } className="sui-grid sui-gap-4 sui-w-full">
              <FormLabel>Enter Verification Code</FormLabel>
              <Input
                fullWidth
                type="tel"
                value={ webAuthnGlobalState.valueOTP }
                onChange={ filterTheInput }
                inputProps={{ maxLength: 6,
                              inputMode: "numeric",
                              pattern: "[0-9]*"}} />
              {errors && <div className="form-input--error">
                <FieldError name="otp" errors={errors}/>
              </div> }
              <Typography>
                Didn&apos;t receive the code?
              </Typography>
              <Link onClick={ handleEmailMeANewCode }> Email me a new code </Link>
                <ButtonGroup fullWidth orientation="vertical">
                  <Button variant="primary" disabled={!webAuthnGlobalState.isOTPValid} onClick={ handleWebAuthnOTPSubmit }>Submit</Button>
                  <Button onClick={ closeWebAuthnModal } >Cancel</Button>
                </ButtonGroup>
            </form>
        </StencilWebAuthnModal>
        <div id="webAuthnOutcome" ref={webAuthRef} />
        <input id="loginButton_0" type="button" onClick={postWebAuthClick} />
    </>
  );
}

export  {
  WebAuthnEnrollment
};